// /map calls: spot catalog, member image delivery and question lookup for spot practice.
// Contract: platform/docs/api-inventory/content-ops.md (section 1) and membership.md (1.10, 1.11).
import type { Call } from './call'
import type { Question } from './index'

/** Province outline: n name, c centre [lng, lat], p polygons -> rings -> [lng, lat] points. */
export interface MapGeometryFeature { n: string; c: [number, number]; p: number[][][][] }
export interface MapGeometry {
  version?: string
  coordinateSystem?: string
  bounds?: unknown
  features: MapGeometryFeature[]
}

/** One row of the public preview: no summary, images or cards. */
export interface MapPreviewEntity {
  id: string
  entityId: string
  name: string
  province: string
  city?: string
  category?: string
  lng: number
  lat: number
  questionCount?: number
}

export interface MapPreview {
  geometry: MapGeometry | null
  entities: MapPreviewEntity[]
  total: number
}

export interface MapKnowledgeCard {
  cardId: string
  title: string
  /** Logical path such as assets/infographics/<slug>.png. */
  image: string
  thumb: string
  order: number
  questionFingerprints: string[]
}

export interface MapCover { image: string; thumb: string; type?: string; order?: number }

/** Member catalog row. Compact rows (list) leave out covers and knowledgeCards. */
export interface MapSpot extends MapPreviewEntity {
  aliases?: string[]
  regions?: string[]
  type?: string
  summary?: string
  facts?: string[]
  tags?: string[]
  covers?: MapCover[]
  knowledgeCards?: MapKnowledgeCard[]
  /** Legacy question fingerprints; resolve to q_ ids before showing questions. */
  questionFingerprints?: string[]
}

export interface MapCatalog {
  geometry: MapGeometry | null
  entities: MapSpot[]
  total: number
}

export interface ResolveResult { resolved: Record<string, string>; unknown: string[] }
export interface QuestionLookup { questions: (Question & { subject?: string; chapter?: string })[]; missing: string[] }
export interface AssetUrl { url: string; expiresAt: number }
export interface ImageDeliveryConfig { enabled: boolean; origin: string; assets: Record<string, string> }

export function createMapApi(call: Call) {
  return {
    /** Public. */
    preview: () => call<MapPreview>({ method: 'GET', path: '/api/native/v1/map/preview' }),
    /** Active members only: 401 AUTH_REQUIRED (no session or guest), 403 MEMBERSHIP_REQUIRED. */
    spots: (params: { province?: string; q?: string; category?: string } = {}) =>
      call<MapCatalog>({ method: 'GET', path: '/api/native/v1/map/spots', query: { compact: 1, ...params } }),
    /** Active members only. Full record with covers and knowledgeCards. */
    spot: async (id: string) =>
      (await call<{ spot: MapSpot }>({ method: 'GET', path: `/api/native/v1/map/spots/${encodeURIComponent(id)}` })).spot,

    /** Answer states of the account keyed by question; the map derives per-spot progress from them. Signed-in accounts only. */
    answerStates: async (): Promise<Record<string, unknown>> => {
      const payload = await call<{ progress?: { answerStates?: Record<string, unknown> }; answerStates?: Record<string, unknown> }>(
        { method: 'GET', path: '/api/native/v1/progress' })
      return payload.progress?.answerStates ?? payload.answerStates ?? {}
    },

    /** Legacy fingerprints or ids -> q_ ids (1..500 per call). */
    resolveQuestions: (ids: string[]) =>
      call<ResolveResult>({ method: 'POST', path: '/api/v1/questions/resolve', body: { ids } }),
    /** Up to 200 q_ ids per call, without answers. */
    questionsByIds: (ids: string[]) =>
      call<QuestionLookup>({ method: 'GET', path: '/api/v1/questions', query: { ids: ids.join(',') } }),

    /** Spots the account has saved (bookmark kind 'spot'). */
    savedSpotIds: async (): Promise<string[]> => {
      const { bookmarks } = await call<{ bookmarks: { kind: string; targetId: string; active: boolean }[] }>(
        { method: 'GET', path: '/api/v1/learning/bookmarks' })
      return bookmarks.filter(b => b.kind === 'spot' && b.active).map(b => b.targetId)
    },

    imageDeliveryConfig: () => call<ImageDeliveryConfig>({ method: 'GET', path: '/api/image-delivery/config' }),
    /** Short-lived signed URL (about 5 minutes). 404 when the CDN signer is not configured. */
    memberAssetUrl: (assetId: string, variant: 'full' | 'thumb' = 'full') =>
      call<AssetUrl>({ method: 'GET', path: `/api/member/assets/${encodeURIComponent(assetId)}/url`, query: { variant } }),
    /** Same-origin URL that streams the original bytes (session cookie required). */
    memberAssetBytesPath: (assetId: string) => `/api/member/assets/${encodeURIComponent(assetId)}`,
  }
}
